import { makeEventNamespace } from "~/core/dom/events";
import { initSection } from "~/core/shopify/init-section";

const SECTION_SELECTOR = ".js-faq-section";
const ROOT_SELECTOR = "[data-faq-root]";
const TAB_SELECTOR = "[data-faq-tab]";
const PANEL_SELECTOR = "[data-faq-panel]";
const NAV_SELECTOR = "[data-faq-nav]";
const LIST_SELECTOR = ".faq__nav-list";
const SCROLLBAR_SELECTOR = "[data-faq-scrollbar]";
const THUMB_SELECTOR = "[data-faq-scrollbar-thumb]";

const JS_CLASS = "faq--js";
const TABS_CLASS = "faq--tabs";
const SCROLL_CLASS = "faq--scroll";
const QUERY_KEY = "category";

/**
 * Scroll mode is desktop-only, and deliberately NOT gated on motion preference.
 *
 * Nothing here moves against the scroll: categories are stacked at their
 * natural heights and the nav highlight is a colour change. "Reduce motion"
 * means remove movement while keeping meaning, and the highlight is the
 * meaning — gating it would leave those users with no position feedback at
 * all. The one genuine movement, smooth scrolling on nav click, is gated
 * separately in scrollToPanel().
 */
const SCROLL_MODE_QUERY = "(min-width: 1025px)";

/** Must match `top` on the sticky nav and panel heading in faq.css. */
const STICKY_OFFSET = 96;

const ENTER_ATTR = "data-faq-entering";
const STAGGER_TARGETS = ".faq__panel-heading, .accordion-details__item";
const STAGGER_PROPERTY = "--faq-stagger-index";
const STAGGER_MAX_INDEX = 8;
const STAGGER_TIMEOUT_MS = 1200;

const prefersReducedMotion = (): boolean =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const getTabs = (root: HTMLElement): HTMLElement[] =>
    Array.from(root.querySelectorAll<HTMLElement>(TAB_SELECTOR));

const getPanels = (root: HTMLElement): HTMLElement[] =>
    Array.from(root.querySelectorAll<HTMLElement>(PANEL_SELECTOR));

const setUrlCategory = (handle: string): void => {
    const url = new URL(window.location.href);

    if (url.searchParams.get(QUERY_KEY) === handle) {
        return;
    }

    url.searchParams.set(QUERY_KEY, handle);
    window.history.replaceState({}, "", url);
};

/** Keeps the active chip visible in the horizontally scrolling mobile bar. */
const revealTab = (tab: HTMLElement): void => {
    const list = tab
        .closest(NAV_SELECTOR)
        ?.querySelector<HTMLElement>(LIST_SELECTOR);

    if (!list || list.scrollWidth <= list.clientWidth) {
        return;
    }

    list.scrollTo({
        left: tab.offsetLeft - list.offsetLeft - 16,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
};

const updateScrollIndicator = (root: HTMLElement): void => {
    const list = root.querySelector<HTMLElement>(LIST_SELECTOR);
    const bar = root.querySelector<HTMLElement>(SCROLLBAR_SELECTOR);
    const thumb = root.querySelector<HTMLElement>(THUMB_SELECTOR);

    if (!list || !bar || !thumb) {
        return;
    }

    const { scrollWidth, clientWidth, scrollLeft } = list;
    const scrollable = scrollWidth - clientWidth > 1;

    bar.hidden = !scrollable;

    if (!scrollable) {
        return;
    }

    const visibleRatio = clientWidth / scrollWidth;
    const maxScroll = scrollWidth - clientWidth;
    const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    // A percentage translate resolves against the element's own *unscaled*
    // width — the full track — not the scaled-down thumb.
    const travel = progress * (1 - visibleRatio) * 100;

    thumb.style.transform = `translateX(${travel}%) scaleX(${visibleRatio})`;
};

const playEnterAnimation = (panel: HTMLElement): void => {
    if (prefersReducedMotion()) {
        return;
    }

    const targets = Array.from(
        panel.querySelectorAll<HTMLElement>(STAGGER_TARGETS),
    );

    if (targets.length === 0) {
        return;
    }

    const clear = (): void => {
        panel.removeAttribute(ENTER_ATTR);

        for (const target of targets) {
            target.style.removeProperty(STAGGER_PROPERTY);
        }
    };

    clear();
    // Reflow between removing and re-adding, so re-selecting the current
    // category replays the animation instead of no-oping.
    void panel.offsetWidth;

    targets.forEach((target, index) => {
        target.style.setProperty(
            STAGGER_PROPERTY,
            String(Math.min(index, STAGGER_MAX_INDEX)),
        );
    });

    panel.setAttribute(ENTER_ATTR, "");

    const last = targets[targets.length - 1];
    const timeout = window.setTimeout(clear, STAGGER_TIMEOUT_MS);

    last?.addEventListener(
        "animationend",
        () => {
            window.clearTimeout(timeout);
            clear();
        },
        { once: true },
    );
};

/** Paints the active nav item. Shared by both modes; ARIA differs per mode. */
const paintActive = (
    root: HTMLElement,
    handle: string,
    mode: "tabs" | "scroll",
): void => {
    for (const tab of getTabs(root)) {
        const isActive = tab.dataset.category === handle;

        // Each mode owns one attribute and clears the other's, so switching
        // modes can't leave a tab carrying both aria-selected and aria-current.
        if (mode === "tabs") {
            tab.removeAttribute("aria-current");
            tab.setAttribute("aria-selected", isActive ? "true" : "false");
            // Only the active tab is in the tab order; arrows move between them.
            tab.tabIndex = isActive ? 0 : -1;
        } else {
            tab.removeAttribute("aria-selected");

            if (isActive) {
                tab.setAttribute("aria-current", "true");
            } else {
                tab.removeAttribute("aria-current");
            }
        }

        if (isActive) {
            revealTab(tab);
        }
    }
};

// ---------------------------------------------------------------------------
// Tab mode — one category at a time. Mobile, and any reduced-motion viewport.
// ---------------------------------------------------------------------------

const applyTabSemantics = (root: HTMLElement): void => {
    root.querySelector(LIST_SELECTOR)?.setAttribute("role", "tablist");

    for (const tab of getTabs(root)) {
        const handle = tab.dataset.category ?? "";
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-controls", `faq-panel-${handle}`);
        tab.closest("li")?.setAttribute("role", "presentation");
    }

    for (const panel of getPanels(root)) {
        const handle = panel.dataset.category ?? "";
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `faq-tab-${handle}`);
    }
};

const clearTabSemantics = (root: HTMLElement): void => {
    root.querySelector(LIST_SELECTOR)?.removeAttribute("role");

    for (const tab of getTabs(root)) {
        tab.removeAttribute("role");
        tab.removeAttribute("aria-controls");
        tab.removeAttribute("aria-selected");
        tab.removeAttribute("tabindex");
        tab.closest("li")?.removeAttribute("role");
    }

    for (const panel of getPanels(root)) {
        const handle = panel.dataset.category ?? "";
        panel.removeAttribute("role");
        // Back to labelling by its own heading, the landmark default.
        panel.setAttribute("aria-labelledby", `faq-heading-${handle}`);
    }
};

const selectCategory = (
    root: HTMLElement,
    handle: string,
    { focusTab = false, updateUrl = true, animate = true } = {},
): void => {
    paintActive(root, handle, "tabs");

    if (focusTab) {
        getTabs(root)
            .find((tab) => tab.dataset.category === handle)
            ?.focus();
    }

    for (const panel of getPanels(root)) {
        const isActive = panel.dataset.category === handle;
        panel.hidden = !isActive;

        if (isActive && animate) {
            playEnterAnimation(panel);
        }
    }

    if (updateUrl) {
        setUrlCategory(handle);
    }
};

const handleKeydown = (root: HTMLElement, event: KeyboardEvent): void => {
    const keys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];

    if (!keys.includes(event.key)) {
        return;
    }

    const tabs = getTabs(root);
    const currentIndex = tabs.findIndex(
        (tab) => tab.getAttribute("aria-selected") === "true",
    );

    if (currentIndex === -1) {
        return;
    }

    let nextIndex = currentIndex;

    if (event.key === "Home") {
        nextIndex = 0;
    } else if (event.key === "End") {
        nextIndex = tabs.length - 1;
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % tabs.length;
    } else {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }

    const handle = tabs[nextIndex]?.dataset.category;

    if (handle) {
        event.preventDefault();
        selectCategory(root, handle, { focusTab: true });
    }
};

const enterTabMode = (root: HTMLElement, initial: string): (() => void) => {
    const namespace = makeEventNamespace();

    root.classList.add(TABS_CLASS);
    root.classList.remove(SCROLL_CLASS);
    applyTabSemantics(root);

    namespace.addDelegatedEventListener(root, TAB_SELECTOR, "click", (tab, evt) => {
        const handle = tab.dataset.category;

        if (handle) {
            evt.preventDefault();
            selectCategory(root, handle);
        }
    });

    namespace.addDirectEventListener(root, "keydown", (_root, event) => {
        handleKeydown(root, event as KeyboardEvent);
    });

    // No stagger on entry: this is the initial paint for the mode, not a switch.
    selectCategory(root, initial, { updateUrl: false, animate: false });

    return () => {
        namespace.destroy();
        clearTabSemantics(root);
        root.classList.remove(TABS_CLASS);

        for (const panel of getPanels(root)) {
            panel.hidden = false;
        }
    };
};

// ---------------------------------------------------------------------------
// Scroll mode — every category stacked, nav tracks scroll position. Desktop.
// ---------------------------------------------------------------------------

/**
 * The panel you are reading is the last one whose top has passed the sticky
 * offset. Computed from live rects rather than from intersection ratios, so it
 * stays correct when an accordion opens and shifts everything below it.
 */
const computeActivePanel = (panels: HTMLElement[]): HTMLElement | undefined => {
    const atPageBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;

    if (atPageBottom) {
        return panels[panels.length - 1];
    }

    let active = panels[0];

    for (const panel of panels) {
        if (panel.getBoundingClientRect().top <= STICKY_OFFSET + 8) {
            active = panel;
        }
    }

    return active;
};

/**
 * `focus` is opt-in because moving focus is only correct when the user asked
 * for it. On a click it carries keyboard users along with the scroll; on page
 * load it would steal focus from the top of the document and paint a
 * focus-visible ring on the first category, unprompted.
 */
const scrollToPanel = (panel: HTMLElement, { focus = false } = {}): void => {
    const top = panel.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET;

    window.scrollTo({
        top,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
    });

    if (focus) {
        panel.focus({ preventScroll: true });
    }
};

const enterScrollMode = (root: HTMLElement, initial: string): (() => void) => {
    const namespace = makeEventNamespace();
    const panels = getPanels(root);

    root.classList.add(SCROLL_CLASS);
    root.classList.remove(TABS_CLASS);
    clearTabSemantics(root);

    for (const panel of panels) {
        panel.hidden = false;
    }

    let current = "";

    const sync = (): void => {
        const active = computeActivePanel(panels);
        const handle = active?.dataset.category;

        if (!handle || handle === current) {
            return;
        }

        current = handle;
        paintActive(root, handle, "scroll");
        setUrlCategory(handle);
    };

    namespace.addDelegatedEventListener(root, TAB_SELECTOR, "click", (tab, evt) => {
        const handle = tab.dataset.category;
        const panel = panels.find((item) => item.dataset.category === handle);

        if (panel) {
            evt.preventDefault();
            scrollToPanel(panel, { focus: true });
        }
    });

    // Scroll drives the sync, throttled to one call per frame.
    //
    // An IntersectionObserver alone is not enough here: it only fires when a
    // panel crosses the observation band, and our panels run up to 1170px
    // against a ~100px band, so crossings are far too sparse — measured, the
    // nav lagged the scroll by the better part of a second.
    let frame: number | null = null;

    const requestSync = (): void => {
        if (frame !== null) {
            return;
        }

        frame = window.requestAnimationFrame(() => {
            frame = null;
            sync();
        });
    };

    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", requestSync);

    // The observer's job is layout change, not position: opening an accordion
    // shifts every panel below it, and this re-runs the sync without needing
    // the polling interval the PDP sections fall back on.
    const observer = new ResizeObserver(requestSync);

    for (const panel of panels) {
        observer.observe(panel);
    }

    // Deep link only. sync() writes ?category= into the URL on every visit, so
    // the parameter alone does not mean the user asked for a category — and
    // scrolling to the first panel would just push the hero off screen on
    // every reload.
    const target = panels.find((panel) => panel.dataset.category === initial);

    if (target && target !== panels[0]) {
        scrollToPanel(target);
    }

    sync();

    return () => {
        window.removeEventListener("scroll", requestSync);
        window.removeEventListener("resize", requestSync);

        if (frame !== null) {
            window.cancelAnimationFrame(frame);
        }

        observer.disconnect();
        namespace.destroy();
        root.classList.remove(SCROLL_CLASS);

        for (const tab of getTabs(root)) {
            tab.removeAttribute("aria-current");
        }
    };
};

// ---------------------------------------------------------------------------

initSection(SECTION_SELECTOR, (section) => {
    const root = section.querySelector<HTMLElement>(ROOT_SELECTOR);

    if (!root || getTabs(root).length === 0) {
        return { unload: null };
    }

    root.classList.add(JS_CLASS);

    const requested = new URL(window.location.href).searchParams.get(QUERY_KEY);
    const known = getTabs(root).some((tab) => tab.dataset.category === requested);
    const initial =
        (known && requested) || getTabs(root)[0]?.dataset.category || "";

    const scrollModeQuery = window.matchMedia(SCROLL_MODE_QUERY);
    let teardownMode: (() => void) | null = null;

    const applyMode = (): void => {
        teardownMode?.();
        teardownMode = scrollModeQuery.matches
            ? enterScrollMode(root, initial)
            : enterTabMode(root, initial);
        updateScrollIndicator(root);
    };

    applyMode();
    scrollModeQuery.addEventListener("change", applyMode);

    const list = root.querySelector<HTMLElement>(LIST_SELECTOR);
    const onListScroll = (): void => updateScrollIndicator(root);

    list?.addEventListener("scroll", onListScroll, { passive: true });

    const resizeObserver =
        typeof ResizeObserver === "undefined"
            ? null
            : new ResizeObserver(onListScroll);

    if (list) {
        resizeObserver?.observe(list);
    }

    return {
        unload: () => {
            scrollModeQuery.removeEventListener("change", applyMode);
            teardownMode?.();
            list?.removeEventListener("scroll", onListScroll);
            resizeObserver?.disconnect();
            root.classList.remove(JS_CLASS);
        },
    };
});
